import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronLeft, faChevronRight, faExpand } from '@fortawesome/free-solid-svg-icons';
import Reveal from '../components/Reveal';
import PageHeader from '../components/PageHeader';
import { useLanguage } from '../context/LanguageContext';
import './Gallery.css';

// Galerie officielle : réalisations avec descriptions riches et valorisantes.
// w/h = dimensions réelles du fichier, pour réserver la place avant chargement.
const GALLERY = [
  { id: 1, file: 'gallery1', category: 'meubles', w: 1200, h: 896 },
  { id: 2, file: 'gallery2', category: 'meubles', w: 810, h: 1080 },
  { id: 3, file: 'gallery3', category: 'meubles', w: 1200, h: 896 },
  { id: 4, file: 'gallery4', category: 'meubles', w: 810, h: 1080 },
  { id: 5, file: 'gallery5', category: 'meubles', w: 1408, h: 768 },
  { id: 6, file: 'gallery6', category: 'decoration', w: 810, h: 1080 },
  { id: 7, file: 'gallery7', category: 'cuisines', w: 1200, h: 896 },
  { id: 8, file: 'gallery8', category: 'meubles', w: 1200, h: 896 },
  { id: 9, file: 'gallery9', category: 'decoration', w: 1200, h: 896 },
  { id: 10, file: 'gallery10', category: 'cuisines', w: 1408, h: 768 },
  { id: 11, file: 'gallery11', category: 'meubles', w: 1200, h: 896 },
  { id: 12, file: 'gallery12', category: 'meubles', w: 1200, h: 896 },
  { id: 13, file: 'gallery13', category: 'cuisines', w: 1280, h: 960 },
  { id: 14, file: 'gallery14', category: 'cuisines', w: 1280, h: 1055 },
  { id: 15, file: 'gallery15', category: 'cuisines', w: 1280, h: 853 },
  { id: 16, file: 'gallery16', category: 'cuisines', w: 1280, h: 853 },
  // Mobilier en panneaux laminés (mélaminé / stratifié) : exemples d'aménagements réalisables
  { id: 17, file: 'lamine/lamine-01-cuisine', category: 'lamine', w: 1376, h: 768 },
  { id: 18, file: 'lamine/lamine-02-dressing', category: 'lamine', w: 1376, h: 768 },
  { id: 19, file: 'lamine/lamine-03-meuble-tv', category: 'lamine', w: 1376, h: 768 },
  { id: 20, file: 'lamine/lamine-04-bureaux', category: 'lamine', w: 1376, h: 768 },
  { id: 21, file: 'lamine/lamine-05-chambre-hotel', category: 'lamine', w: 1376, h: 768 },
  { id: 22, file: 'lamine/lamine-06-salle-de-bain', category: 'lamine', w: 1376, h: 768 },
  { id: 23, file: 'lamine/lamine-07-accueil', category: 'lamine', w: 1376, h: 768 }
];

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('tous');
  const [openIndex, setOpenIndex] = useState(null);
  const touchStartX = useRef(null);
  const { t } = useLanguage();

  const galleryImages = GALLERY.map((image) => {
    const key = `gal${image.id}`;
    return {
      ...image,
      src: `/img/${image.file}.webp`,
      alt: t(`${key}_alt`),
      title: t(`${key}_title`),
      description: t(`${key}_desc`)
    };
  });

  // On n'affiche que les filtres qui contiennent au moins une image
  const categories = [
    { id: 'tous', name: t('cat_all') },
    { id: 'meubles', name: t('cat_furniture') },
    { id: 'cuisines', name: t('cat_kitchen') },
    { id: 'lamine', name: t('cat_lamine') },
    { id: 'decoration', name: t('cat_decor') },
    { id: 'restauration', name: t('cat_renovation') }
  ]
    .map((cat) => ({
      ...cat,
      count: cat.id === 'tous'
        ? galleryImages.length
        : galleryImages.filter((img) => img.category === cat.id).length
    }))
    .filter((cat) => cat.count > 0);

  const filteredImages = selectedCategory === 'tous'
    ? galleryImages
    : galleryImages.filter((img) => img.category === selectedCategory);

  const current = openIndex !== null ? filteredImages[openIndex] : null;

  const close = useCallback(() => setOpenIndex(null), []);
  const show = useCallback((delta) => {
    setOpenIndex((i) => (i === null ? i : (i + delta + filteredImages.length) % filteredImages.length));
  }, [filteredImages.length]);

  // Clavier (Échap, flèches) et blocage du défilement quand la visionneuse est ouverte
  useEffect(() => {
    if (openIndex === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') show(1);
      if (e.key === 'ArrowLeft') show(-1);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [openIndex, close, show]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) show(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return (
    <div className="gallery-page">
      <PageHeader
        image="/img/gallery3.webp"
        eyebrow={t('gallery_eyebrow')}
        title={t('gallery_header_title')}
        subtitle={t('gallery_header_subtitle')}
      />

      {/* Filtres : une seule ligne, défilement horizontal sur mobile */}
      <div className="gallery-filters">
        <div className="container">
          <div className="filter-buttons" role="tablist">
            {categories.map((category) => (
              <button
                key={category.id}
                role="tab"
                aria-selected={selectedCategory === category.id}
                className={`filter-btn ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
                <span className="filter-count">{category.count}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="gallery-grid-section">
        <div className="container">
          <div className="gallery-grid">
            {filteredImages.map((image, index) => (
              <Reveal
                as="button"
                type="button"
                key={`${selectedCategory}-${image.id}`}
                delay={(index % 3) * 70}
                animation="fade"
                className="gallery-item"
                onClick={() => setOpenIndex(index)}
                aria-label={image.title}
              >
                <div className="img-frame" style={{ aspectRatio: `${image.w} / ${image.h}` }}>
                  <img src={image.src} alt={image.alt} width={image.w} height={image.h} loading="lazy" />
                  <div className="gallery-overlay">
                    <FontAwesomeIcon icon={faExpand} className="gallery-expand" />
                    <h3>{image.title}</h3>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Visionneuse */}
      {current && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={current.title}
          onClick={close}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <button className="lightbox-btn lightbox-close" onClick={close} aria-label={t('gallery_close')}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
          {filteredImages.length > 1 && (
            <>
              <button
                className="lightbox-btn lightbox-prev"
                onClick={(e) => { e.stopPropagation(); show(-1); }}
                aria-label={t('gallery_prev')}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button
                className="lightbox-btn lightbox-next"
                onClick={(e) => { e.stopPropagation(); show(1); }}
                aria-label={t('gallery_next')}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </>
          )}
          <figure className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img key={current.src} src={current.src} alt={current.alt} />
            <figcaption>
              <span className="lightbox-counter">{openIndex + 1} / {filteredImages.length}</span>
              <h3>{current.title}</h3>
              <p>{current.description}</p>
            </figcaption>
          </figure>
        </div>
      )}

      <section className="section section-alt cta-band">
        <Reveal as="div" className="container">
          <h2>{t('gallery_cta_title')}</h2>
          <p>{t('gallery_cta_subtitle')}</p>
          <Link to="/contact" className="btn btn-primary">{t('nav_quote_btn')}</Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Gallery;
